/**
 * Emoji Picker Component
 * 
 * A grid-based emoji picker for selecting emojis in board editing
 * with scrollable categories and all emojis, plus custom image upload
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Upload } from "lucide-react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

// Extended emoji categories with more variety
const EMOJI_CATEGORIES = {
  emotions: [
    "😊", "😢", "😣", "😰", "🤒", "😴", "😌", "😟", "😤", "😡", 
    "🥺", "😫", "🤢", "🤕", "😷", "🥰", "😍", "🤗", "😎", "🥳",
    "😭", "😱", "🤯", "😵", "🥴", "🤔", "😐", "😑", "🙄", "😬",
    "😮", "😯", "😲", "😳", "🥱", "😪", "🤤", "😋", "😛", "😜",
    "🤪", "🤑", "🤠", "😈", "👿", "💀", "☠️", "👻", "👽", "🤖"
  ],
  needs: [
    "💧", "🍽️", "🚽", "💊", "🛏️", "🪥", "🧴", "👕", "🧦", "👓", 
    "📱", "💡", "🔔", "⏰", "📺", "🍞", "☕", "🧊", "🧹", "🪑",
    "🚿", "🛁", "🧻", "🧼", "🧽", "🪒", "👚", "👖", "👟", "🧥",
    "🧤", "🧣", "👒", "🎒", "💼", "🍼", "🥤", "🍵", "🍶", "🧃",
    "🍕", "🍔", "🍟", "🌭", "🥪", "🥗", "🍲", "🍜", "🍛", "🍣"
  ],
  medical: [
    "💨", "🩺", "💉", "🩹", "♿", "🦽", "🩼", "🌡️", "❤️", "🫀", 
    "🫁", "🦷", "👂", "👁️", "🧠", "💪", "🦴", "🩸", "🏥", "⚕️",
    "💊", "🧬", "🔬", "🧪", "🩻", "🩺", "🏨", "🚑", "🚁", "💔",
    "❤️‍🩹", "💗", "💓", "💕", "🫂", "🦿", "🦾", "👃", "👅", "🦶",
    "🦵", "🤲", "🤌", "👐", "🙌", "🤞", "✌️", "🤟", "🤘", "👌"
  ],
  actions: [
    "🔄", "⬆️", "⬇️", "➡️", "⬅️", "✋", "👆", "👇", "👈", "👉", 
    "🙏", "👍", "👎", "✅", "❌", "🤝", "👋", "🖐️", "✊", "🫡",
    "↗️", "↘️", "↙️", "↖️", "↕️", "↔️", "🔃", "🔁", "🔀", "⏩",
    "⏪", "⏫", "⏬", "▶️", "⏸️", "⏹️", "⏺️", "⏏️", "🔼", "🔽",
    "🖱️", "🖲️", "💻", "⌨️", "🖥️", "📤", "📥", "📧", "📨", "📩"
  ],
  alerts: [
    "❗", "⚠️", "🆘", "🚨", "📢", "🔊", "🔇", "⏸️", "▶️", "⏹️", 
    "🔃", "🔁", "🔀", "📞", "✉️", "🔒", "🔓", "🔑", "⏱️", "🛎️",
    "🔕", "🔔", "📣", "📯", "🎺", "💬", "💭", "🗯️", "💡", "🔦",
    "🕯️", "🧯", "🚒", "🚓", "🚐", "🚑", "📍", "🗺️", "🧭", "⚡",
    "🔥", "💥", "🌪️", "🌊", "⛈️", "🌧️", "☔", "🔴", "🟠", "🟢"
  ],
  people: [
    "👨‍⚕️", "👩‍⚕️", "👨", "👩", "👶", "🧒", "🧑", "👧", "👦", "🧓", 
    "👴", "👵", "🧑‍🤝‍🧑", "👨‍👩‍👧", "🤱", "🧑‍🦽", "🧑‍🦯", "🚶", "🧍", "👮",
    "🧑‍🔧", "🧑‍🍳", "👷", "🧑‍⚕️", "👩‍👦", "👩‍👧", "👨‍👦", "👨‍👧", "👪", "👨‍👩‍👦",
    "👩‍👩‍👦", "👨‍👨‍👦", "🙋", "🙋‍♂️", "🙋‍♀️", "🙆", "🙆‍♂️", "🙆‍♀️", "🙅", "🙅‍♂️",
    "🙇", "🙇‍♂️", "🙇‍♀️", "💁", "💁‍♂️", "💁‍♀️", "🧏", "🧏‍♂️", "🧏‍♀️", "🤷"
  ],
  nature: [
    "🌞", "🌙", "⭐", "🌧️", "❄️", "🌈", "🌸", "🌿", "🍀", "🐕", 
    "🐈", "🦜", "🐠", "🦋", "🌺", "🌻", "🌲", "🍎", "🌊", "☀️",
    "🌤️", "⛅", "🌥️", "☁️", "🌦️", "🌨️", "❄️", "💨", "💫", "🌟",
    "✨", "🌏", "🌍", "🌎", "🌑", "🌒", "🌓", "🌔", "🌕", "🌖",
    "🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼", "🐨", "🐯"
  ],
  objects: [
    "🏠", "🚗", "🛒", "📖", "🎵", "🎮", "🎨", "✏️", "📝", "🗓️", 
    "💰", "🎁", "🎂", "🧸", "🪴", "🛋️", "🖼️", "📷", "💻", "🎧",
    "📚", "📕", "📗", "📘", "📙", "📓", "📔", "📒", "📃", "📄",
    "📑", "🗒️", "🗓️", "📆", "📅", "🗃️", "🗄️", "🗑️", "🔒", "🔐",
    "🔑", "🗝️", "🔨", "🪓", "⛏️", "🔧", "🔩", "⚙️", "🪛", "🧰"
  ],
  symbols: [
    "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💗",
    "💖", "💝", "💘", "💟", "❣️", "💔", "❤️‍🔥", "❤️‍🩹", "♥️", "♦️",
    "♠️", "♣️", "🔴", "🟠", "🟡", "🟢", "🔵", "🟣", "⚫", "⚪",
    "🟤", "🔺", "🔻", "🔸", "🔹", "🔶", "🔷", "◾", "◽", "▪️",
    "▫️", "⬛", "⬜", "🟥", "🟧", "🟨", "🟩", "🟦", "🟪", "⭕"
  ],
  hands: [
    "👍", "👎", "👊", "✊", "🤛", "🤜", "👏", "🙌", "👐", "🤲",
    "🤝", "🙏", "✍️", "💅", "🤳", "💪", "🦾", "🦿", "🦵", "🦶",
    "👂", "🦻", "👃", "🧠", "🫀", "🫁", "🦷", "🦴", "👀", "👁️",
    "👅", "👄", "👶", "🧒", "👦", "👧", "🧑", "👱", "👨", "🧔",
    "🤌", "🤏", "👌", "🤞", "✌️", "🤟", "🤘", "🤙", "👈", "👉"
  ],
};

type CategoryKey = keyof typeof EMOJI_CATEGORIES;

interface EmojiPickerProps {
  selectedEmoji: string;
  onEmojiSelect: (emoji: string) => void;
  isHebrew?: boolean;
}

export function EmojiPicker({ selectedEmoji, onEmojiSelect, isHebrew = false }: EmojiPickerProps) {
  const [customImage, setCustomImage] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<CategoryKey>("emotions");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollViewportRef = useRef<HTMLDivElement>(null);

  const categoryLabels: Record<CategoryKey, { he: string; en: string }> = {
    emotions: { he: "רגשות", en: "Emotions" },
    needs: { he: "צרכים", en: "Needs" },
    medical: { he: "רפואי", en: "Medical" },
    actions: { he: "פעולות", en: "Actions" },
    alerts: { he: "התראות", en: "Alerts" },
    people: { he: "אנשים", en: "People" },
    nature: { he: "טבע", en: "Nature" },
    objects: { he: "חפצים", en: "Objects" },
    symbols: { he: "סמלים", en: "Symbols" },
    hands: { he: "ידיים", en: "Hands" },
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setCustomImage(base64);
        onEmojiSelect(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const scrollToCategory = (category: CategoryKey) => {
    const element = categoryRefs.current[category];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Track scroll position to update active category
  const handleScroll = useCallback(() => {
    const viewport = scrollViewportRef.current;
    if (!viewport) return;

    const scrollTop = viewport.scrollTop;
    const allCategories = Object.keys(EMOJI_CATEGORIES) as CategoryKey[];
    
    let currentCategory: CategoryKey = allCategories[0];
    
    for (const category of allCategories) {
      const element = categoryRefs.current[category];
      if (element) {
        const offsetTop = element.offsetTop - viewport.offsetTop;
        if (scrollTop >= offsetTop - 50) {
          currentCategory = category;
        }
      }
    }
    
    setActiveCategory(currentCategory);
  }, []);

  const isCustomImage = selectedEmoji.startsWith("data:image");
  const allCategories = Object.keys(EMOJI_CATEGORIES) as CategoryKey[];

  return (
    <div className="space-y-4">
      {/* Selected emoji/image display */}
      <div className="flex items-center justify-center gap-4 p-4 bg-muted rounded-xl">
        {isCustomImage ? (
          <img src={selectedEmoji} alt="Custom" className="w-16 h-16 object-contain rounded-lg" />
        ) : (
          <span className="text-6xl">{selectedEmoji}</span>
        )}
      </div>

      {/* Upload custom image button */}
      <div className="flex justify-center">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
        />
        <Button
          variant="outline"
          onClick={triggerFileUpload}
          className="gap-2"
        >
          <Upload className="w-5 h-5" />
          {isHebrew ? "העלאת תמונה" : "Upload Image"}
        </Button>
      </div>

      {/* Category quick-jump tabs with active indicator */}
      <ScrollArea className="w-full whitespace-nowrap" dir={isHebrew ? "rtl" : "ltr"}>
        <div className="flex gap-2 pb-3 px-1">
          {allCategories.map((category) => (
            <Button
              key={category}
              variant={activeCategory === category ? "default" : "outline"}
              size="sm"
              onClick={() => scrollToCategory(category)}
              className={cn(
                "whitespace-nowrap shrink-0 transition-all",
                activeCategory === category && "ring-2 ring-primary/50"
              )}
            >
              {isHebrew 
                ? categoryLabels[category].he 
                : categoryLabels[category].en}
            </Button>
          ))}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* All emojis in one scrollable area - no sticky headers */}
      <ScrollAreaPrimitive.Root className="relative overflow-hidden h-64 rounded-xl bg-muted/50">
        <ScrollAreaPrimitive.Viewport 
          ref={scrollViewportRef}
          className="h-full w-full rounded-[inherit]"
          onScroll={handleScroll}
        >
          <div className="p-3 space-y-4">
            {allCategories.map((category) => (
              <div 
                key={category}
                ref={(el) => { categoryRefs.current[category] = el; }}
              >
                {/* Emoji grid for this category */}
                <div className="grid grid-cols-8 gap-1">
                  {EMOJI_CATEGORIES[category].map((emoji, index) => (
                    <button
                      key={`${category}-${emoji}-${index}`}
                      onClick={() => onEmojiSelect(emoji)}
                      className={cn(
                        "text-2xl p-1.5 rounded-lg transition-all hover:bg-primary/20 hover:scale-110",
                        selectedEmoji === emoji && "bg-primary/30 ring-2 ring-primary"
                      )}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    </div>
  );
}
